import { logGreyError } from 'owa-analytics';
import {
    getItem,
    getLocalStorage,
    getOwaPrefixedKey,
    type LocalStorageKeys,
    removeItem,
    setItem,
} from 'owa-local-storage';
import { isCachingEnabled } from './isCachingEnabled';

const LOCAL_STORAGE_KEY_PREFIX = 'AccountBootstrapCache';

interface VersionedCache<T> {
    version: number;
    value: T;
}

function getLocalStorageKey(persistenceId: string, name: string): LocalStorageKeys {
    return `${LOCAL_STORAGE_KEY_PREFIX}_${persistenceId}_${name}`;
}

export function tryGetVersionedCacheValue<T>(
    persistenceId: string,
    name: string,
    version: number
): T | undefined {
    try {
        const key = getLocalStorageKey(persistenceId, name);
        const cachedValue = getItem(self, key);
        if (cachedValue) {
            const parsedValue = JSON.parse(cachedValue) as VersionedCache<T>;
            if (parsedValue.version === version) {
                return parsedValue.value;
            }
        }
    } catch (error) {
        /* eslint-disable owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
         * BASELINE: DO NOT COPY/PASTE!
         *	> Argument at index 1 for logGreyError must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
        // Errors resulting from enabling useUnknownInCatchVariables
        // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'Error | undefined'.
        // @ts-expect-error
        logGreyError('ErrorReadingAccountBootstrapCache', undefined, error, {
            /* eslint-enable owa-custom-rules/telemetry-mailboxinfo-as-second-param */
            persistenceId,
            name,
            version,
        });
    }

    return undefined;
}

export function setVersionedCacheValue<T>(
    persistenceId: string,
    name: string,
    version: number,
    value: T
): void {
    const key = getLocalStorageKey(persistenceId, name);
    if (isCachingEnabled()) {
        const versionedValue: VersionedCache<T> = { value, version };
        setItem(self, key, JSON.stringify(versionedValue));
    } else {
        removeItem(self, key);
    }
}

/**
 * Find and delete any cache entries left behind for accounts that no longer exist.
 */
export function removeOrphanedCacheEntries(persistentIdsToKeep: string[]): void {
    const storage = getLocalStorage(self);
    const prefix = getOwaPrefixedKey(LOCAL_STORAGE_KEY_PREFIX);
    for (const key of Object.keys(storage)) {
        if (key.startsWith(prefix)) {
            const pieces = key.split('_');
            if (pieces.length === 3 && persistentIdsToKeep.indexOf(pieces[1]) === -1) {
                removeItem(self, key as LocalStorageKeys);
            }
        }
    }
}
